var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { defineComponent as _defineComponent } from 'vue';
import { BAlert } from "bootstrap-vue";
import { storeToRefs } from "pinia";
import { computed, onMounted, ref, set as VueSet, unref, watch } from "vue";
import { isHistoryItem, userOwnsHistory } from "@/api";
import { copyDataset } from "@/api/datasets";
import ExpandedItems from "@/components/History/Content/ExpandedItems";
import SelectedItems from "@/components/History/Content/SelectedItems";
import { HistoryFilters } from "@/components/History/HistoryFilters";
import { deleteContent, updateContentFields } from "@/components/History/model/queries";
import { Toast } from "@/composables/toast";
import { useActiveElement } from "@/composables/useActiveElement";
import { startWatchingHistory } from "@/store/historyStore/model/watchHistory";
import { useEventStore } from "@/stores/eventStore";
import { useHistoryItemsStore } from "@/stores/historyItemsStore";
import { useHistoryStore } from "@/stores/historyStore";
import { useUserStore } from "@/stores/userStore";
import { getOperatorForAlias } from "@/utils/filtering";
import { setDrag } from "@/utils/setDrag";
import HistoryCounter from "./HistoryCounter.vue";
import HistoryDetails from "./HistoryDetails.vue";
import HistoryDropZone from "./HistoryDropZone.vue";
import HistoryEmpty from "./HistoryEmpty.vue";
import HistoryMessages from "./HistoryMessages.vue";
import HistoryOperations from "./HistoryOperations/HistoryOperations.vue";
import OperationErrorDialog from "./HistoryOperations/OperationErrorDialog.vue";
import SelectionChangeWarning from "./HistoryOperations/SelectionChangeWarning.vue";
import HistorySelectionOperations from "./HistoryOperations/SelectionOperations.vue";
import HistorySelectionStatus from "./HistoryOperations/SelectionStatus.vue";
import FilterMenu from "@/components/Common/FilterMenu.vue";
import ContentItem from "@/components/History/Content/ContentItem.vue";
import ListingLayout from "@/components/History/Layout/ListingLayout.vue";
import LoadingSpan from "@/components/LoadingSpan.vue";
export default /*#__PURE__*/ _defineComponent({
    __name: 'HistoryPanel',
    props: {
        listOffset: { default: 0 },
        history: null,
        filter: { default: "" },
        filterable: { type: Boolean, default: false },
        isMultiViewItem: { type: Boolean, default: false }
    },
    setup(__props) {
        const props = __props;
        const filterClass = HistoryFilters;
        const filterText = ref("");
        const invisibleHistoryItems = ref({});
        const isLoading = ref(false);
        const offsetQueryParam = ref(0);
        const searchError = ref(undefined);
        const showAdvanced = ref(false);
        const showDropZone = ref(false);
        const operationRunning = ref(null);
        const operationError = ref(null);
        const querySelectionBreak = ref(false);
        const dragTarget = ref(null);
        const contentItemRefs = computed(() => {
            return historyItems.value.reduce((acc, item) => {
                acc[itemUniqueKey(item)] = ref(null);
                return acc;
            }, {});
        });
        const currItemFocused = useActiveElement();
        const lastItemId = ref(null);
        const { currentFilterText, currentHistoryId } = storeToRefs(useHistoryStore());
        const { lastCheckedTime, totalMatchesCount, isWatching } = storeToRefs(useHistoryItemsStore());
        const historyStore = useHistoryStore();
        const historyItemsStore = useHistoryItemsStore();
        const { currentUser } = storeToRefs(useUserStore());
        const currentUserOwnsHistory = computed(() => {
            return userOwnsHistory(currentUser.value, props.history);
        });
        const canEditHistory = computed(() => {
            return currentUserOwnsHistory.value && !props.history.deleted && !props.history.archived;
        });
        const historyUpdateTime = computed(() => {
            return props.history.update_time;
        });
        const queryKey = computed(() => {
            return `${props.history.id}-${filterText.value}`;
        });
        const queryDefault = computed(() => {
            return !filterText.value;
        });
        const isProcessing = computed(() => {
            if (!operationRunning.value) {
                return false;
            }
            return (operationRunning === null || operationRunning === void 0 ? void 0 : operationRunning.value) >= props.history.update_time;
        });
        const historyItems = computed(() => {
            return historyItemsStore.getHistoryItems(props.history.id, filterText.value);
        });
        const visibleHistoryItems = computed(() => {
            return historyItems.value.filter((item) => !invisibleHistoryItems.value[item.hid]);
        });
        const formattedSearchError = computed(() => {
            const newError = unref(searchError);
            if (!newError) {
                return null;
            }
            const { column, col, operation, op, value, val, err_msg, ValueError } = newError;
            const alias = operation || op;
            const operator = alias ? getOperatorForAlias(alias) : ":";
            return {
                filter: `${column || col}${operator}`,
                value: value || val,
                msg: err_msg,
                typeError: ValueError,
            };
        });
        const storeFilterText = computed(() => {
            if (props.history.id !== currentHistoryId.value) {
                return "";
            }
            return currentFilterText.value || "";
        });
        const lastItemFocused = computed(() => {
            return lastItemId.value ? historyItems.value.find((item) => lastItemId.value === `dataset-${item.id}`) : null;
        });
        watch(offsetQueryParam, () => loadHistoryItems());
        watch(() => queryKey.value, () => {
            invisibleHistoryItems.value = {};
            offsetQueryParam.value = 0;
            loadHistoryItems();
            lastItemId.value = null;
        });
        watch(() => props.filter, (newVal) => (filterText.value = newVal));
        watch(filterText, (newFilterTextValue) => {
            if (props.filterable) {
                historyStore.setFilterText(props.history.id, newFilterTextValue);
            }
        });
        watch(storeFilterText, (newFilterTextValue) => {
            if (props.filterable) {
                filterText.value = newFilterTextValue;
            }
        });
        watch(historyUpdateTime, () => __awaiter(this, void 0, void 0, function* () { return yield loadHistoryItems(); }));
        watch(() => props.history.id, (newValue, currentValue) => {
            if (newValue !== currentValue) {
                operationRunning.value = null;
                lastItemId.value = null;
            }
        });
        watch(historyItems, (newHistoryItems) => {
            if (!invisibleHistoryItems.value) {
                return;
            }
            // Re-hide invisible history items when `historyItems` changes
            for (const newHistoryItem of newHistoryItems) {
                if (invisibleHistoryItems.value[newHistoryItem.hid]) {
                    VueSet(invisibleHistoryItems.value, newHistoryItem.hid, false);
                }
            }
        });
        watch(() => currItemFocused.value, (newItem, oldItem) => {
            var _a;
            // if user clicked elsewhere, set lastItemId to the last focused item
            // (if it was indeed a history .content-item)
            if (newItem && ((_a = oldItem === null || oldItem === void 0 ? void 0 : oldItem.classList) === null || _a === void 0 ? void 0 : _a.contains("content-item")) && (oldItem === null || oldItem === void 0 ? void 0 : oldItem.getAttribute("data-hid"))) {
                lastItemId.value = (oldItem === null || oldItem === void 0 ? void 0 : oldItem.id) || null;
            }
        });
        function dragSameHistory() {
            return getDragData().sameHistory;
        }
        function getDragData() {
            var _a;
            const eventStore = useEventStore();
            const dragItems = eventStore.getDragItems();
            // Filter out any non-history items
            const historyItems = dragItems === null || dragItems === void 0 ? void 0 : dragItems.filter((item) => isHistoryItem(item));
            const historyId = (_a = historyItems === null || historyItems === void 0 ? void 0 : historyItems[0]) === null || _a === void 0 ? void 0 : _a.history_id;
            return { data: historyItems, sameHistory: historyId === props.history.id, multiple: (historyItems === null || historyItems === void 0 ? void 0 : historyItems.length) > 1 };
        }
        function getHighlight(item) {
            if (unref(isLoading)) {
                return undefined;
            }
            const highlightsKey = parseInt(filterClass.getFilterValue(unref(filterText), "related"));
            if (!highlightsKey) {
                return undefined;
            }
            if (highlightsKey === item.hid) {
                return "active";
            }
            if (item.hid > highlightsKey) {
                return "output";
            }
            return "input";
        }
        function hasMatches(items) {
            return !!items && items.length > 0;
        }
        function isDataset(item) {
            return item.history_content_type === "dataset";
        }
        function loadHistoryItems() {
            return __awaiter(this, void 0, void 0, function* () {
                isLoading.value = true;
                try {
                    yield historyItemsStore.fetchHistoryItems(props.history.id, filterText.value, offsetQueryParam.value);
                    searchError.value = undefined;
                }
                catch (error) {
                    if (error.response && error.response.data && error.response.data.err_msg) {
                        console.debug("HistoryPanel - Load items error:", error.response.data.err_msg);
                        searchError.value = error.response.data;
                    }
                    else {
                        console.debug("HistoryPanel - Load items error.", error);
                    }
                }
                finally {
                    isLoading.value = false;
                }
            });
        }
        function onDelete(item, recursive = false) {
            return __awaiter(this, void 0, void 0, function* () {
                isLoading.value = true;
                setInvisible(item);
                try {
                    yield deleteContent(item, { recursive: recursive });
                    updateContentStats();
                }
                finally {
                    isLoading.value = false;
                }
            });
        }
        function onHideSelection(selectedItems) {
            for (const item of selectedItems) {
                setInvisible(item);
            }
        }
        function onScroll(newOffset) {
            offsetQueryParam.value = newOffset;
        }
        function onUndelete(item) {
            return __awaiter(this, void 0, void 0, function* () {
                setInvisible(item);
                isLoading.value = true;
                try {
                    yield updateContentFields(item, { deleted: false });
                    updateContentStats();
                }
                finally {
                    isLoading.value = false;
                }
            });
        }
        function onUnhide(item) {
            return __awaiter(this, void 0, void 0, function* () {
                setInvisible(item);
                isLoading.value = true;
                try {
                    yield updateContentFields(item, { visible: true });
                    updateContentStats();
                }
                finally {
                    isLoading.value = false;
                }
            });
        }
        function updateContentStats() {
            historyStore.updateContentStats(props.history.id);
        }
        function reloadContents() {
            startWatchingHistory();
        }
        function setInvisible(item) {
            VueSet(unref(invisibleHistoryItems), item.hid, true);
        }
        function onTagChange(item, newTags) {
            item.tags = newTags;
        }
        function onOperationError(error) {
            console.debug("HistoryPanel - Operation error.", error);
            operationError.value = error;
        }
        function onDragEnter(e) {
            if (dragSameHistory()) {
                return;
            }
            dragTarget.value = e.target;
            showDropZone.value = true;
        }
        function onDragOver(e) {
            if (dragSameHistory()) {
                return;
            }
            e.preventDefault();
        }
        function onDragLeave(e) {
            if (dragSameHistory()) {
                return;
            }
            if (dragTarget.value === e.target) {
                showDropZone.value = false;
            }
        }
        function onDrop() {
            return __awaiter(this, void 0, void 0, function* () {
                showDropZone.value = false;
                const { data, sameHistory, multiple } = getDragData();
                if (!data || sameHistory) {
                    return;
                }
                let datasetCount = 0;
                let collectionCount = 0;
                try {
                    // iterate over the data array and copy each item to the current history
                    for (const item of data) {
                        const dataSource = item.history_content_type === "dataset" ? "hda" : "hdca";
                        yield copyDataset(item.id, props.history.id, item.history_content_type, dataSource);
                        if (item.history_content_type === "dataset") {
                            datasetCount++;
                            if (!multiple) {
                                Toast.info("Dataset copied to history");
                            }
                        }
                        else {
                            collectionCount++;
                            if (!multiple) {
                                Toast.info("Collection copied to history");
                            }
                        }
                    }
                    if (multiple && datasetCount > 0) {
                        Toast.info(`${datasetCount} dataset${datasetCount > 1 ? "s" : ""} copied to new history`);
                    }
                    if (multiple && collectionCount > 0) {
                        Toast.info(`${collectionCount} collection${collectionCount > 1 ? "s" : ""} copied to new history`);
                    }
                    historyStore.loadHistoryById(props.history.id);
                }
                catch (error) {
                    Toast.error(`${error}`);
                }
            });
        }
        function updateFilterValue(filterKey, newValue) {
            const currentFilterText = filterText.value;
            filterText.value = filterClass.setFilterValue(currentFilterText, filterKey, newValue);
        }
        function getItemKey(item) {
            return itemUniqueKey(item);
        }
        function itemUniqueKey(item) {
            return `${item.history_content_type}-${item.id}`;
        }
        onMounted(() => __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            // `filterable` here indicates if this is the current history panel
            if (props.filterable && !props.filter) {
                filterText.value = storeFilterText.value;
            }
            yield loadHistoryItems();
            // if there is a listOffset, we are coming from a collection view, so focus on item at that offset
            if (props.listOffset) {
                const itemAtOffset = historyItems.value[props.listOffset];
                if (itemAtOffset) {
                    const itemElement = (_b = (_a = contentItemRefs.value[itemUniqueKey(itemAtOffset)]) === null || _a === void 0 ? void 0 : _a.value) === null || _b === void 0 ? void 0 : _b.$el;
                    itemElement === null || itemElement === void 0 ? void 0 : itemElement.focus();
                }
            }
        }));
        function arrowNavigate(item, eventKey) {
            var _a, _b;
            let nextItem = null;
            if (eventKey === "ArrowDown") {
                nextItem = historyItems.value[historyItems.value.indexOf(item) + 1];
            }
            else if (eventKey === "ArrowUp") {
                nextItem = historyItems.value[historyItems.value.indexOf(item) - 1];
            }
            if (nextItem) {
                const itemElement = (_b = (_a = contentItemRefs.value[itemUniqueKey(nextItem)]) === null || _a === void 0 ? void 0 : _a.value) === null || _b === void 0 ? void 0 : _b.$el;
                itemElement === null || itemElement === void 0 ? void 0 : itemElement.focus();
            }
            return nextItem;
        }
        function setItemDragstart(item, itemIsSelected, selectedItems, selectionSize, event) {
            if (itemIsSelected && selectionSize > 1) {
                const selectedItemsObj = {};
                for (const [key, value] of selectedItems) {
                    selectedItemsObj[key] = value;
                }
                setDrag(event, selectedItemsObj, true);
            }
            else {
                setDrag(event, item);
            }
        }
        return { __sfc: true, props, filterClass, filterText, invisibleHistoryItems, isLoading, offsetQueryParam, searchError, showAdvanced, showDropZone, operationRunning, operationError, querySelectionBreak, dragTarget, contentItemRefs, currItemFocused, lastItemId, currentFilterText, currentHistoryId, lastCheckedTime, totalMatchesCount, isWatching, historyStore, historyItemsStore, currentUser, currentUserOwnsHistory, canEditHistory, historyUpdateTime, queryKey, queryDefault, isProcessing, historyItems, visibleHistoryItems, formattedSearchError, storeFilterText, lastItemFocused, dragSameHistory, getDragData, getHighlight, hasMatches, isDataset, loadHistoryItems, onDelete, onHideSelection, onScroll, onUndelete, onUnhide, updateContentStats, reloadContents, setInvisible, onTagChange, onOperationError, onDragEnter, onDragOver, onDragLeave, onDrop, updateFilterValue, getItemKey, itemUniqueKey, arrowNavigate, setItemDragstart, BAlert, userOwnsHistory, ExpandedItems, SelectedItems, HistoryCounter, HistoryDetails, HistoryDropZone, HistoryEmpty, HistoryMessages, HistoryOperations, OperationErrorDialog, SelectionChangeWarning, HistorySelectionOperations, HistorySelectionStatus, FilterMenu, ContentItem, ListingLayout, LoadingSpan };
    }
});
